import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../Utils/Axios';

export const registerHospital = createAsyncThunk(
  'hospital/register',
  async (hospitalData, thunkAPI) => {
    try {
      const response = await api.post('/api/hospital/register', hospitalData);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || 'Hospital registration failed'
      );
    }
  }
);

export const loginHospital = createAsyncThunk(
  'hospital/login',
  async (credentials, thunkAPI) => {
    try {
      const response = await api.post('/api/hospital/login', credentials);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || 'Hospital login failed'
      );
    }
  }
);

export const verifyHospitalEmailOtp = createAsyncThunk(
  'hospital/verifyEmailOtp',
  async ({ email, otp }, thunkAPI) => {
    try {
      const response = await api.post('/api/hospital/verify-email-otp', { email, otp });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || 'OTP verification failed'
      );
    }
  }
);

export const resendHospitalVerificationOtp = createAsyncThunk(
  'hospital/resendVerificationOtp',
  async (email, thunkAPI) => {
    try {
      const response = await api.post('/api/hospital/resend-verification-otp', { email });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || 'Could not resend OTP'
      );
    }
  }
);

export const fetchHospitalDashboard = createAsyncThunk(
  'hospital/fetchDashboard',
  async (_, thunkAPI) => {
    try {
      const response = await api.get('/api/hospital/dashboard');
      return response.data.dashboard;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch hospital dashboard'
      );
    }
  }
);

export const fetchHospitalJobs = createAsyncThunk(
  'hospital/fetchJobs',
  async (_, thunkAPI) => {
    try {
      const response = await api.get('/api/hospital/jobs');
      return response.data.jobs || [];
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch hospital jobs'
      );
    }
  }
);

export const createHospitalJob = createAsyncThunk(
  'hospital/createJob',
  async (payload, thunkAPI) => {
    try {
      const response = await api.post('/api/hospital/jobs', payload);
      return response.data.job;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        status: error.response?.status,
        message: error.response?.data?.message || error.message || 'Failed to post hospital job',
        amountDueUSD: error.response?.data?.amountDueUSD,
      });
    }
  }
);

const initialState = {
  hospitalInfo: localStorage.getItem('HospitalInfo')
    ? JSON.parse(localStorage.getItem('HospitalInfo'))
    : null,
  dashboard: null,
  jobs: [],
  status: 'idle',
  error: null,
};

const hospitalSlice = createSlice({
  name: 'hospital',
  initialState,
  reducers: {
    logout: (state) => {
      state.hospitalInfo = null;
      state.dashboard = null;
      state.jobs = [];
      localStorage.removeItem('HospitalInfo');
    },
    setHospitalInfo: (state, action) => {
      state.hospitalInfo = action.payload || null;
      if (action.payload) {
        localStorage.setItem('HospitalInfo', JSON.stringify(action.payload));
      } else {
        localStorage.removeItem('HospitalInfo');
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerHospital.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(registerHospital.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(registerHospital.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      .addCase(loginHospital.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginHospital.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.hospitalInfo = action.payload;
        localStorage.setItem('HospitalInfo', JSON.stringify(action.payload));
      })
      .addCase(loginHospital.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      .addCase(verifyHospitalEmailOtp.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(verifyHospitalEmailOtp.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(verifyHospitalEmailOtp.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      .addCase(resendHospitalVerificationOtp.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(resendHospitalVerificationOtp.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(resendHospitalVerificationOtp.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchHospitalDashboard.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchHospitalDashboard.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.dashboard = action.payload;
      })
      .addCase(fetchHospitalDashboard.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchHospitalJobs.fulfilled, (state, action) => {
        state.jobs = action.payload;
      })
      .addCase(fetchHospitalJobs.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(createHospitalJob.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createHospitalJob.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.jobs = [action.payload, ...(state.jobs || [])];
      })
      .addCase(createHospitalJob.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.message || action.error.message;
      });
  },
});

export const { logout, setHospitalInfo } = hospitalSlice.actions;
export default hospitalSlice.reducer;
